import React, { useState, useEffect, useContext } from "react";

import {
  Typography,
  Card,
  CardContent,
  Grid2,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useMediaQuery,
  useTheme,
  CardActions,
  Divider,
} from "@mui/material";
import MoreVert from "@mui/icons-material/MoreVert";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import { CreateOutlined, CloseOutlined } from "@mui/icons-material";

import {
  createPredefinedText,
  deletePredefinedTextById,
  getPredefinedTextById,
  getAllPredefinedText,
  updatePredefinedTextById,
} from "../API/PredefinedTextApi";

import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";
import { showLoadingImage } from "../Utility/Utility";
import MenuBar from "../common/MenuBar";
import { DataGrid } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import ConfirmationDialog from "../common/ConfirmationDialog";
import { UserRoles, UserRolesConstants } from "../Utility/Constants";

const PredefinedTextList = () => {
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const [PredefinedText, setPredefinedText] = useState([]);
  const [editContent, seteditContent] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newContent, setnewContent] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [openEditDialog, setopenEditDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setdeleteId] = useState(null);
  const [pageSize, setPageSize] = useState(5);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Check if user is facility admin
  const isFacilityAdmin = auth?.roles?.includes(UserRolesConstants.FacilityAdmin) || false;
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);

  // Add menu handler functions (after existing functions)
  const handleActionsMenuOpen = (event, row) => {
    setActionsMenuAnchor(event.currentTarget);
    setCurrentRow(row);
  };

  const handleMenuClose = () => {
    setActionsMenuAnchor(null);
    setCurrentRow(null);
  };

  // Load predefined texts on component mount
  useEffect(() => {
    loadPredefinedTexts();
  }, [auth, auth.selectedFacility.id]);

  const loadPredefinedTexts = async () => {
    if (!auth || !auth.selectedFacility.id) return;

    setSaving(true);

    try {
      const result = await getAllPredefinedText(auth);
      if (result.isSuccess) {
        const dataWithSerial = result.data.map((item, index) => ({
          ...item,
          serialNumber: index + 1,
        }));

        setPredefinedText(dataWithSerial || []);
      } else {
        showSnackbar(
          result.data?.message || "Error loading predefined texts",
          "error"
        );
      }
    } catch (error) {
      showSnackbar("Error loading predefined texts: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = async () => {
    if (!isFacilityAdmin) {
      showSnackbar("Only facility admins can add predefined texts", "warning");
      return;
    }

    if (!newContent.trim() || !newTitle.trim()) {
      showSnackbar("Both title and content are required", "warning");
      return;
    }

    setSaving(true);

    try {
      const result = await createPredefinedText(auth, {
        title: newTitle.trim(),
        content: newContent.trim(),
        facilityId: auth.selectedFacility.id,
      });

      if (result.isSuccess) {
        await loadPredefinedTexts();
        setnewContent("");
        setNewTitle("");
        setOpenAddDialog(false);
        showSnackbar("Text added successfully", "success");
      } else {
        showSnackbar(result.data?.message || "Error adding text", "error");
      }
    } catch (error) {
      showSnackbar("Error adding text: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id) => {
    if (!isFacilityAdmin) {
      showSnackbar("Only facility admins can edit predefined texts", "warning");
      return;
    }

    setSaving(true);
    setopenEditDialog(true);
    setEditId(id);

    try {
      const result = await getPredefinedTextById(auth, id);

      if (result.isSuccess) {
        setEditTitle(result.data.title);
        seteditContent(result.data.content);
      } else {
        showSnackbar(result.data?.message || "Error in Updating text", "error");
      }
    } catch (error) {
      showSnackbar("Error in Updating text: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (id) => {
    if (!isFacilityAdmin) {
      showSnackbar(
        "Only facility admins can update predefined texts",
        "warning"
      );
      return;
    }

    if (!editId) {
      showSnackbar("No text selected for editing", "error");
      return;
    }
    setSaving(true);

    try {
      const result = await updatePredefinedTextById(auth, {
        id: editId,
        title: editTitle.trim(),
        content: editContent.trim(),
      });

      if (result.isSuccess) {
        await loadPredefinedTexts();
        showSnackbar("Text updated successfully", "success");
      } else {
        showSnackbar(result.data?.message || "Error updating text", "error");
      }
    } catch (error) {
      showSnackbar("Error updating text: " + error.message, "error");
    } finally {
      handleCloseEditDialog();
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    if (!isFacilityAdmin) {
      showSnackbar(
        "Only facility admins can delete predefined texts",
        "warning"
      );
      return;
    }
    setdeleteId(id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    setSaving(true);
    try {
      const result = await deletePredefinedTextById(auth, deleteId);

      if (result.isSuccess) {
        showSnackbar("Text deleted successfully", "success");
        await loadPredefinedTexts();
      } else {
        showSnackbar(result.data?.message || "Error deleting text", "error");
      }
    } catch (error) {
      showSnackbar("Error deleting text: " + error.message, "error");
    } finally {
      setSaving(false);
      setOpenDeleteDialog(false);
      setdeleteId(null);
    }
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setnewContent("");
    setNewTitle("");
  };

  const handleCloseEditDialog = () => {
    setopenEditDialog(false);
    setEditTitle("");
    seteditContent("");
    setEditId(null);
  };

  const predefinedColumns = [
    {
      field: "serialNumber",
      headerName: "#",
      width: 50,
    },

    {
      field: "title",
      headerName: "Title",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span>{params.row.title}</span>

          {/* Three dots menu - only show if facility admin */}
          {isFacilityAdmin && (
            <Tooltip title="Actions" arrow>
              <IconButton
                size="small"
                onClick={(event) => handleActionsMenuOpen(event, params.row)}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "content",
      headerName: "Content",
      flex: 2,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.4,
            fontSize: "0.95rem",
          }}
        >
          <p style={{ margin: 0 }}>
            {params?.value?.length <= 100
              ? params.value
              : `${params?.value?.slice(0, 100)}...`}
          </p>
        </div>
      ),
    },
    {
      field: "createdByUserName",
      headerName: " Created By ",
      flex: 1,
      renderCell: (params) => (
        <div
          style={{
            lineHeight: 1.3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <strong>{`${params.row.createdByUserName} `}</strong>
          <span style={{ color: "#555", fontSize: "0.85em" }}>
            {convertUTCToTimeZoneDateTimeString(
              params.row.createdOn,
              auth?.selectedFacility?.timeZone
            )}
          </span>
        </div>
      ),
    },
    {
      field: "lastModifiedByUserName",
      headerName: "Last Modified By ",
      flex: 1,
      renderCell: (params) => (
        <div
          style={{
            lineHeight: 1.3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <strong>{`${params.row.lastModifiedByUserName} `}</strong>
          <span style={{ color: "#555", fontSize: "0.85em" }}>
            {convertUTCToTimeZoneDateTimeString(
              params.row.lastModifiedOn,
              auth?.selectedFacility?.timeZone
            )}
          </span>
        </div>
      ),
    },

    // Only show actions column if user is facility admin
    ...(isFacilityAdmin
      ? [
          // {
          //   field: "actions",
          //   headerName: "Actions",
          //   width: 100,
          //   renderCell: (params) => (
          //     <>
          //       <Tooltip title="Edit Predefined Text" arrow>
          //         <IconButton onClick={() => handleEdit(params?.row.id)}>
          //           <EditIcon fontSize="small" />
          //         </IconButton>
          //       </Tooltip>
          //       <Tooltip title="Remove Predefined Text" arrow>
          //         <IconButton onClick={() => handleDelete(params?.row.id)}>
          //           <DeleteOutlineIcon fontSize="small" />
          //         </IconButton>
          //       </Tooltip>
          //     </>
          //   ),
          // },
        ]
      : []),
  ];

  const renderPredefinedTextCards = () => (
    <Card sx={{ px: 1 }}>
      {PredefinedText.map((data) => (
        <Card key={data.id} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <div>
              <Typography variant="caption">
                Serial Number : {data.serialNumber}
              </Typography>
              <Typography variant="h6">{data.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {data.content}
              </Typography>
            </div>
            <Divider sx={{ my: 1 }} />

            <Typography variant="body2">
              Created By : {data.createdByUserName}
            </Typography>
            <Typography variant="body2">
              Created On :
              {convertUTCToTimeZoneDateTimeString(
                data.createdOn,
                auth?.selectedFacility?.timeZone
              )}
            </Typography>
            <Typography variant="body2">
              Last Modified By : {data.lastModifiedByUserName}
            </Typography>
            <Typography variant="body2">
              Last Modified On :{" "}
              {convertUTCToTimeZoneDateTimeString(
                data.lastModifiedOn,
                auth?.selectedFacility?.timeZone
              )}
            </Typography>
          </CardContent>

          <CardActions
            sx={{ justifyContent: "flex-end", px: 2, pb: 2, mt: -1 }}
          >
            <Tooltip title="Edit Predefined Text" arrow>
              <IconButton
                size="small"
                color="primary"
                disabled={saving || !isFacilityAdmin}
                onClick={() => handleEdit(data.id)}
              >
                <CreateOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove Predefined Text" arrow>
              <IconButton
                size="small"
                color="secondary"
                onClick={() => handleDelete(data.id)}
                disabled={saving || !isFacilityAdmin}
              >
                <CloseOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </CardActions>
        </Card>
      ))}
    </Card>
  );

  return (
    <Grid2
      container
      sx={{
        maxHeight: "calc(100vh - 120px)",
        overflowY: "auto",
      }}
    >
      {saving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301,
          }}
        >
          {showLoadingImage()}
        </div>
      )}
      <MenuBar
        key="PredefinedText"
        showNew={true}
        onNew={() => setOpenAddDialog(true)}
        newLabel="New Text"
        newTooltip="New Text"
        disabled={!isFacilityAdmin}
      />
      {/* date display */}
      <Card sx={{ width: "100%", padding: "5px", boxShadow: "none" }}>
        {isMobile ? (
          renderPredefinedTextCards()
        ) : (
          <DataGrid
            localeText={{
              noRowsLabel:
                "There are no Predefined Text to display. Change the dates or create a new Predefined Text.",
            }}
            loading={showSpinner}
            slotProps={{
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
            }}
            rows={PredefinedText}
            columns={predefinedColumns}
            pageSize={5}
            density="compact"
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            autoHeight
            rowHeight={55}
            sx={{
              width: "100%",
              "& .sticky-column": {
                position: "sticky",
                right: 0, // Keep it on the right side
                backgroundColor: "white", // To make sure it has a solid background
                zIndex: 100, // Ensure it stays on top of other columns
              },
              "& .MuiTablePagination-toolbar": {
                minHeight: "unset",
                alignItems: "center",
                paddingTop: "0 !important",
                paddingBottom: "0 !important",
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  margin: 0,
                },
            }}
          />
        )}
      </Card>

      {/* dialogs*/}
      <div>
        {/* Add New Text Dialog */}
        <Dialog
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Predefined Text</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <TextField
                autoFocus
                label="Title"
                fullWidth
                variant="outlined"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
              <TextField
                label="Content"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={newContent}
                onChange={(e) => setnewContent(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={handleCloseAddDialog}
              color="inherit"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddNew}
              variant="contained"
              color="primary"
              disabled={!newContent.trim() || !newTitle.trim() || saving}
            >
              Add Text
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update text dialog */}
        <Dialog
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Update Predefined Text</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <TextField
                autoFocus
                label="Title"
                fullWidth
                variant="outlined"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
              <TextField
                label="Content"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={editContent}
                onChange={(e) => seteditContent(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={handleCloseEditDialog}
              color="inherit"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleSaveEdit()}
              variant="contained"
              color="primary"
              disabled={!editContent.trim() || !editTitle.trim() || saving}
            >
              Update Text
            </Button>
          </DialogActions>
        </Dialog>

        {/* delete Confirmation dialog */}
        <ConfirmationDialog
          title="Delete Predefined Text"
          statement="Are you sure you want to delete this predefined text? This action cannot be undone."
          open={openDeleteDialog}
          okButtonColor="error"
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={confirmDelete}
        />
        <Menu
          anchorEl={actionsMenuAnchor}
          open={Boolean(actionsMenuAnchor)}
          onClose={handleMenuClose}
        >
          {currentRow && isFacilityAdmin && (
            <>
              <MenuItem
                onClick={() => {
                  handleEdit(currentRow.id);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                Edit Text
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleDelete(currentRow.id);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <DeleteOutlineIcon fontSize="small" />
                </ListItemIcon>
                Delete Text
              </MenuItem>
            </>
          )}
        </Menu>
      </div>
    </Grid2>
  );
};

export default PredefinedTextList;
